import React, {
  useContext,
} from 'react';
import clsx from 'clsx';
import {
  CellContext,
  HeaderContext,
  Table,
} from '@tanstack/react-table';
import sum from 'lodash/sum';
import head from 'lodash/head';
import uniqBy from 'lodash/uniqBy';
import IconButton from '@mui/material/IconButton';
import {
  formatMoney,
} from '@populateui/utils';
import {
  ServiceAllocation,
  VisitInfo,
} from 'pages/Dashboard/pages/Billing/types';
import AmountCell from 'pages/Dashboard/pages/Billing/Payments/AmountCell';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import AdjustmentCodeAmountCell, {
  AdjustmentBucketField,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/AdjustmentCodeAmountCell';
import EOBAdjustmentCodePopover from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBAdjustmentCodePopover';
import ToolTip from 'core/components/Tooltip';
import {
  DeleteIcon,
} from 'core/icons';
import {
  billingEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  hasAllocationData,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import {
  isServiceLineInactive,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/serviceLineStatus';
import {
  EditableCurrencyCell,
  ReadOnlyCurrencyCell,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EditableInputs';
import {
  useSelectableContext,
} from 'core/hooks';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from 'core/icons/chevronRight';
import {
  isClaimExpandable,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ClaimVisitSummary';
import isNil from 'lodash/isNil';
import RemarkCodesPopover from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/RemarkCodesPopover';
import ClaimPaymentRemarkCodesPopover from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ClaimPaymentRemarkCodesPopover';
import EOBServiceCodeCell, {
  ReadOnlyServiceCode,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBServiceCodeCell';
import NotesIcon from 'core/icons/notes';
import EOBActions from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBActions';
import Link from 'core/components/Link';
import EOBServiceCodeActionCell from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBServiceCodeActionCell';
import {
  WizardContext,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/wizardContext';
import map from 'lodash/map';

type CountableIdType = 'copay'
  | 'deductible'
  | 'coinsurance'
  | 'insurancePayment'
  | 'nonCovered'
  | 'insuranceAdjustments'
  | 'autoWriteOff'
  | 'total';

export function getEOBServiceAllocationRowId(
  row: ServiceAllocation,
  index: number,
) {
  const serviceCodeId = row?.visitNoteBillingCodeId
    ?? row?.visitNoteBillingCustomServiceCodeId
    ?? row?.id
    ?? 0;

  return [
    row?.claim?.claimId ?? row?.claimId ?? 0,
    row?.eobServiceCodePaymentInfoId ?? 0,
    row?.eobId ?? 0,
    serviceCodeId,
    index,
  ].join('-');
}

const AggregateCell = ({
  column,
  table,
}: HeaderContext<ServiceAllocation, unknown>) => formatMoney(
  sum(table.getRowModel().rows
    .map(({ original }) => original[column.columnDef.id as CountableIdType] ?? 0)),
);

function BillTotalAggregateCell({ table }: HeaderContext<ServiceAllocation, unknown>) {
  const { rows } = table.getRowModel();
  const uniqueBilledAmounts = uniqBy(rows, ({
    original,
  }) => original?.visitNoteBillingCodeId ?? original?.id).map(({
    original,
  }) => original?.billedAmount ?? 0);

  return formatMoney(sum(uniqueBilledAmounts));
}

const EOBCurrencyCell = ({
  row,
  ...props
}: CellContext<ServiceAllocation, unknown>) => (
  isServiceLineInactive(row.original)
    ? <ReadOnlyCurrencyCell {...props} row={row} />
    : <EditableCurrencyCell {...props} row={row} />
);

function PatientReviewNameCell({
  row,
}: CellContext<ServiceAllocation, unknown>) {
  const { patients, addPatient, setInReview } = useContext(WizardContext);
  const { patientId, patientFullName } = row.original;
  const patient = patients.find((item) => item.patientId === patientId);

  return (
    <button
      type="button"
      onClick={() => {
        if (!isNil(patient)) {
          addPatient(patient);
          setInReview(false);
        }
      }}
      className="text-blue-500 font-semibold hover:underline cursor-pointer text-left truncate"
    >
      {patientFullName}
    </button>
  );
}

export const eobReviewColumn: ColumnDef<ServiceAllocation> = {
  id: 'eobReviewName',
  accessorKey: 'patientFullName',
  columnTitle: 'Patient',
  size: 250,
  cell: PatientReviewNameCell,
};

function getServiceCodeCell(readonly: boolean = false) {
  return ({ row }: CellContext<ServiceAllocation, unknown>) => (readonly
    ? <ReadOnlyServiceCode data={row.original} />
    : (
      <EOBServiceCodeCell
        data={row.original}
        index={row.index}
      />
    ));
}
const serviceCodeColumn: ColumnDef<ServiceAllocation> = {
  id: 'serviceName',
  accessorKey: 'serviceName',
  columnTitle: 'Services',
  footer: 'Total',
  minSize: 150,
  meta: { autoSized: true },
  multiLineHeader: true,
  className: 'font-semibold truncate',
  cell: getServiceCodeCell(),
};

const readonlyServiceCodeColumn: ColumnDef<ServiceAllocation> = {
  ...serviceCodeColumn,
  cell: getServiceCodeCell(true),
};

const billTotalColumn: ColumnDef<ServiceAllocation> = {
  id: 'billedAmount',
  accessorKey: 'billedAmount',
  columnTitle: (
    <>
      Billed
      <br />
      Amount
    </>
  ),
  multiLineHeader: true,
  footer: BillTotalAggregateCell,
  cell: ({ row, column }) => (
    row.original?.suppressBilledAmount
      ? <span>—</span>
      : (
        <AmountCell<ServiceAllocation>
          value={row.original?.billedAmount ?? 0}
          columnDef={column.columnDef}
          showZero
          showNegative={false}
        />
      )
  ),
};

const allowedAmountColumn: ColumnDef<ServiceAllocation> = {
  id: 'allowedAmount',
  accessorKey: 'allowedAmount',
  columnTitle: (
    <>
      Allowed
      <br />
      Amount
    </>
  ),
  footer: AggregateCell,
  multiLineHeader: true,
  cell: EOBCurrencyCell,
};

const coPayColumn: ColumnDef<ServiceAllocation> = {
  id: 'copay',
  accessorKey: 'copay',
  columnTitle: 'Copay',
  footer: AggregateCell,
  multiLineHeader: true,
  cell: EOBCurrencyCell,
};

const deductibleColumn: ColumnDef<ServiceAllocation> = {
  id: 'deductible',
  accessorKey: 'deductible',
  columnTitle: 'Deductible',
  footer: AggregateCell,
  multiLineHeader: true,
  cell: EOBCurrencyCell,
};

const coInsuranceColumn: ColumnDef<ServiceAllocation> = {
  id: 'coinsurance',
  accessorKey: 'coinsurance',
  columnTitle: 'Co-insurance',
  footer: AggregateCell,
  multiLineHeader: true,
  cell: EOBCurrencyCell,
};

const nonCoveredColumn: ColumnDef<ServiceAllocation> = {
  id: 'nonCovered',
  accessorKey: 'nonCovered',
  columnTitle: (
    <>
      Cash /
      <br />
      Non Covered
    </>
  ),
  footer: AggregateCell,
  multiLineHeader: true,
  cell: EOBCurrencyCell,
};

const insuranceAdjustmentsColumn: ColumnDef<ServiceAllocation> = {
  id: 'insuranceAdjustments',
  accessorKey: 'insuranceAdjustments',
  columnTitle: (
    <>
      Insurance
      <br />
      Adjustments
    </>
  ),
  footer: AggregateCell,
  multiLineHeader: true,
  meta: { autoSized: true },
  minSize: 120,
  maxSize: 180,
  cell: ({ row }) => (
    <AdjustmentCodeAmountCell
      row={row}
      field="insuranceAdjustments"
    />
  ),
};

const autoWriteOffColumn: ColumnDef<ServiceAllocation> = {
  id: 'autoWriteOff',
  accessorKey: 'autoWriteOff',
  columnTitle: (
    <>
      Auto
      <br />
      Write-Off
    </>
  ),
  footer: AggregateCell,
  multiLineHeader: true,
  meta: { autoSized: true },
  minSize: 120,
  maxSize: 180,
  cell: ({ row }) => (
    <AdjustmentCodeAmountCell
      row={row}
      field="autoWriteOff"
    />
  ),
};

function getClaimAdjustmentCell(readonly: boolean = false) {
  return ({ row }: CellContext<ServiceAllocation, unknown>) => (
    <EOBAdjustmentCodePopover
      data={row.original}
      index={row.index}
      readonly={readonly || isServiceLineInactive(row.original)}
    />
  );
}

function getHealthCareRemarkCodesCell(readonly: boolean = false) {
  return ({ row }: CellContext<ServiceAllocation, unknown>) => (
    <RemarkCodesPopover
      data={row.original}
      index={row.index}
      readonly={readonly || isServiceLineInactive(row.original)}
    />
  );
}

const claimAdjustmentsColumn: ColumnDef<ServiceAllocation> = {
  id: 'claimAdjustments',
  accessorKey: 'adjustment',
  columnTitle: 'CARC',
  tooltip: 'Claim Adjustment Reason Code',
  size: 150,
  minSize: 120,
  maxSize: 180,
  meta: { autoSized: true },
  multiLineHeader: true,
  cell: getClaimAdjustmentCell(),
};

const healthCareRemarkCodesColumn: ColumnDef<ServiceAllocation> = {
  id: 'healthCareRemarkCodes',
  accessorKey: 'healthCareRemarkCodes',
  columnTitle: 'RARC',
  tooltip: 'Remittance Advice Remark Code',
  size: 110,
  multiLineHeader: true,
  cell: getHealthCareRemarkCodesCell(),
};

const readonlyClaimAdjustmentsColumn: ColumnDef<ServiceAllocation> = {
  ...claimAdjustmentsColumn,
  cell: getClaimAdjustmentCell(true),
};

const readonlyHealthCareRemarkCodesColumn: ColumnDef<ServiceAllocation> = {
  ...healthCareRemarkCodesColumn,
  cell: getHealthCareRemarkCodesCell(true),
};

const insurancePaymentColumn: ColumnDef<ServiceAllocation> = {
  id: 'insurancePayment',
  accessorKey: 'insurancePayment',
  columnTitle: (
    <>
      Insurance
      <br />
      Payment
    </>
  ),
  footer: AggregateCell,
  multiLineHeader: true,
  cell: EOBCurrencyCell,
};

const totalColumn: ColumnDef<ServiceAllocation> = {
  id: 'total',
  accessorKey: 'total',
  columnTitle: 'Total',
  footer: AggregateCell,
  size: 90,
  cell: ({ row }) => (
    <AmountCell
      value={row?.original?.total}
      showZero
    />
  ),
};

const eobNumberColumn: ColumnDef<ServiceAllocation> = {
  id: 'eobNumber',
  accessorKey: 'eobNumber',
  columnTitle: 'EOB',
  size: 240,
  className: 'font-semibold',
};

type ClearButtonProps = {
  className?: string;
  tooltip: string;
  disabled: boolean;
  onClick: () => void;
};

function ClearButton({
  tooltip,
  disabled,
  onClick,
  className,
}: ClearButtonProps) {
  return (
    <ToolTip title={tooltip}>
      <div className={clsx('!w-full flex items-center justify-center', {
        'cursor-not-allowed': disabled,
      })}
      >
        <IconButton
          color="error"
          className="!p-1"
          disabled={disabled}
          onClick={onClick}
          tabIndex={-1}
        >
          <DeleteIcon className={className} />
        </IconButton>
      </div>
    </ToolTip>
  );
}

const actionsColumn: ColumnDef<ServiceAllocation> = {
  id: 'actions',
  size: 64,
  columnTitle: (table: Table<ServiceAllocation>) => {
    const data = table.getRowModel()?.rows ?? [];
    const disabled = !hasAllocationData(map(data, 'original'));
    return (
      <ClearButton
        tooltip={disabled ? 'Nothing to clear' : 'Clear All'}
        disabled={disabled}
        onClick={() => {
          paymentsEventChannel.emit('onEOBAllocationDelete', head(data)?.original?.claim?.claimId ?? 0);
        }}
        className="!w-5 !h-5 !text-zinc-800"
      />
    );
  },
  cell: EOBServiceCodeActionCell,
};

export const eobColumns: ColumnDef<ServiceAllocation>[] = [
  serviceCodeColumn,
  ...[
    billTotalColumn,
    allowedAmountColumn,
    insurancePaymentColumn,
    coPayColumn,
    deductibleColumn,
    coInsuranceColumn,
    nonCoveredColumn,
  ].map((column) => ({
    ...column,
    maxSize: 180,
    minSize: 76,
    meta: { autoSized: true },
  })),
  insuranceAdjustmentsColumn,
  autoWriteOffColumn,
  claimAdjustmentsColumn,
  healthCareRemarkCodesColumn,
  actionsColumn,
];

const readonlyColumns: ColumnDef<ServiceAllocation>[] = [
  readonlyServiceCodeColumn,
  ...eobColumns
    .slice(1, 11)
    .map(
      ({ cell, ...column }) => {
        if (column.id === 'insuranceAdjustments') {
          return {
            ...column,
            cell: ({ row }: CellContext<ServiceAllocation, unknown>) => (
              <AdjustmentCodeAmountCell
                row={row}
                field="insuranceAdjustments"
                readonly
              />
            ),
          };
        }

        if (column.id === 'autoWriteOff') {
          return {
            ...column,
            cell: ({ row }: CellContext<ServiceAllocation, unknown>) => (
              <AdjustmentCodeAmountCell
                row={row}
                field="autoWriteOff"
                readonly
              />
            ),
          };
        }

        if (column.id === 'billedAmount') {
          return {
            ...column,
            cell: ({ row, ...props }: CellContext<ServiceAllocation, unknown>) => (
              row.original?.suppressBilledAmount
                ? <span>—</span>
                : <ReadOnlyCurrencyCell {...props} row={row} />
            ),
          };
        }

        return {
          ...column,
          cell: ReadOnlyCurrencyCell,
        };
      },
    ),
];

const claimPaymentRemarkCodesReviewColumn: ColumnDef<ServiceAllocation> = {
  id: 'claimPaymentRemarkCodes',
  accessorKey: 'eobClaimPayments',
  columnTitle: 'CPRC',
  tooltip: 'Claim Payment Remark Code',
  size: 120,
  cell: ({ row }) => (
    <ClaimPaymentRemarkCodesPopover
      data={row.original as VisitInfo}
      readonly
    />
  ),
};

export const eobReadonlyColumns: ColumnDef<ServiceAllocation>[] = [
  ...readonlyColumns.slice(0, 10),
  readonlyClaimAdjustmentsColumn,
  readonlyHealthCareRemarkCodesColumn,
  ...readonlyColumns.slice(11),
];

export const eobRelatedReadonlyColumns: ColumnDef<ServiceAllocation>[] = [
  eobNumberColumn,
  readonlyServiceCodeColumn,
  ...eobReadonlyColumns.slice(1),
];
export const eobReviewColumns: ColumnDef<ServiceAllocation>[] = [
  eobReviewColumn,
  ...eobReadonlyColumns.slice(1).map((column) => {
    if (column.id === 'insuranceAdjustments' || column.id === 'autoWriteOff') {
      return {
        ...column,
        cell: ({ row }: CellContext<ServiceAllocation, unknown>) => (
          <AdjustmentCodeAmountCell
            row={row}
            field={column.id as AdjustmentBucketField}
            readonly
            isExplicitEdit
          />
        ),
      };
    }

    return column;
  }),
  claimPaymentRemarkCodesReviewColumn,
];

export const statementColumns: ColumnDef<ServiceAllocation>[] = [
  coPayColumn,
  deductibleColumn,
  coInsuranceColumn,
  nonCoveredColumn,
  totalColumn,
].map(({ cell, meta, ...column }) => ({
  ...column,
  maxSize: 90,
  size: 90,
  minSize: 80,
  cell: ReadOnlyCurrencyCell,
}));

export const invoiceColumns: ColumnDef<ServiceAllocation>[] = [
  readonlyServiceCodeColumn,
  ...statementColumns,
];

function ClaimIdCell({
  row,
}: CellContext<VisitInfo, unknown>) {
  const { isSelected, toggle } = useSelectableContext();
  const expandable = isClaimExpandable(row.original);
  const expanded = isSelected(row.original?.claim?.claimId ?? '');
  const { externalId, claimId } = row.original?.claim ?? {};
  const Icon = expanded ? ExpandMoreIcon : ChevronRightIcon;
  let tooltip = 'No Service Codes';

  if (expandable) {
    tooltip = expanded ? 'Collapse' : 'Expand';
  }

  return (
    <div className="flex items-center">
      <ToolTip title={tooltip}>
        <div>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggle(claimId ?? '');
            }}
            className={clsx('!p-1', { '!cursor-not-allowed opacity-30': !expandable })}
            disableRipple={!expandable}
            disabled={!expandable}
          >
            <Icon className="!text-gray-500" />
          </IconButton>
        </div>
      </ToolTip>
      <Link
        text={externalId}
        href={`/billing/claims/${claimId}`}
        target="_blank"
        classes={{ label: 'font-semibold' }}
      />
    </div>
  );
}

function RelatedAllocationsCell({
  row,
}: CellContext<VisitInfo, unknown>) {
  const {
    relatedAllocation = [],
    hasEOBs,
    claimId,
  } = row.original;
  const { isSelected } = useSelectableContext();

  return (
    <div className="flex items-center">
      <ToolTip
        hide={!hasEOBs}
        title="EOB Posted"
      >
        <div className="font-semibold w-4 flex items-center justify-center">
          {hasEOBs ? (
            <CheckCircleIcon className="!text-emerald-500 !text-base" />
          ) : '--'}
        </div>
      </ToolTip>
      <ToolTip title={relatedAllocation.length === 0 ? 'No Related EOB Data Available' : 'Show Related EOB Data'}>
        <div>
          <IconButton
            color="info"
            disabled={relatedAllocation.length === 0}
            onClick={(e) => {
              if (isSelected(claimId ?? '')) {
                e.stopPropagation();
              }
              billingEventChannel.emit('onRelatedAllocation', [
                e.currentTarget?.closest('.populate-table-row') as HTMLElement,
                relatedAllocation,
              ]);
            }}
          >
            <NotesIcon className="!w-4 !h-4 !text-inherit" />
          </IconButton>
        </div>
      </ToolTip>
    </div>
  );
}

const claimIdColumn: ColumnDef<VisitInfo> = {
  id: 'claimId',
  columnTitle: 'Claim',
  size: 220,
  cell: ClaimIdCell,
};

const insuranceNameColumn: ColumnDef<VisitInfo> = {
  id: 'insuranceName',
  columnTitle: 'Payer',
  meta: { autoSized: true },
  cell: ({ row }) => (
    <div className="font-semibold truncate">
      <span className={clsx({ 'text-gray-500': isNil(row.original?.claim?.insuranceName) })}>
        {row.original?.claim?.insuranceName ?? 'Not Set'}
      </span>
    </div>
  ),
};

const claimDateColumn: ColumnDef<VisitInfo> = {
  id: 'formattedVisitDate',
  accessorKey: 'formattedVisitDate',
  columnTitle: (
    <>
      Date of
      <br />
      Service
    </>
  ),
  size: 90,
  cell: ({ row }) => (
    <div className="font-semibold">
      {row.original?.formattedVisitDate}
    </div>
  ),
};

const appointmentTypeColumn: ColumnDef<VisitInfo> = {
  id: 'typeOfVisitDescription',
  columnTitle: 'Appointment',
  meta: { autoSized: true },
  cell: ({ row }) => (
    <div className="font-semibold truncate">
      {row.original?.typeOfVisitDescription}
    </div>
  ),
};

const unpostedColumn: ColumnDef<VisitInfo> = {
  id: 'unallocated',
  columnTitle: 'Unposted',
  size: 100,
  cell: ({ row: { original: { unallocated } } }) => (
    <ToolTip
      title={unallocated !== 0
        ? `Billed amount unposted by ${formatMoney(unallocated ?? 0)}`
        : null}
      hide={unallocated === 0}
    >
      <div className={clsx('font-semibold', {
        'text-red-500': unallocated !== 0,
      })}
      >
        {formatMoney(unallocated ?? 0)}
      </div>
    </ToolTip>
  ),
};

const insurancePaymentPostedColumn: ColumnDef<VisitInfo> = {
  id: 'postedInsurancePayment',
  columnTitle: (
    <>
      Insurance
      <br />
      Payment
    </>
  ),
  size: 100,
  cell: ({ row: { original: { insurancePaymentTotal } } }) => (
    <div className={clsx('font-semibold', {
      'text-gray-700': insurancePaymentTotal !== 0,
      'text-gray-500': insurancePaymentTotal === 0,
    })}
    >
      {formatMoney(insurancePaymentTotal ?? 0)}
    </div>
  ),
};

const claimPaymentRemarkCodesColumn: ColumnDef<VisitInfo> = {
  id: 'claimPaymentRemarkCodes',
  accessorKey: 'eobClaimPayments',
  columnTitle: 'CPRC',
  tooltip: 'Claim Payment Remark Code',
  size: 160,
  cell: ({ row }) => (
    <ClaimPaymentRemarkCodesPopover
      data={row.original}
    />
  ),
};

const relatedAllocationsColumn: ColumnDef<VisitInfo> = {
  id: 'relatedAllocation',
  columnTitle: '',
  size: 52,
  cell: RelatedAllocationsCell,
};

export const claimActionsColumn: ColumnDef<VisitInfo> = {
  id: 'claimActions',
  columnTitle: '',
  size: 32,
  cell: ({ row }) => <EOBActions {...row.original} />,
};

export const claimHeaderColumns: ColumnDef<VisitInfo>[] = [
  claimIdColumn,
  insuranceNameColumn,
  claimDateColumn,
  appointmentTypeColumn,
  unpostedColumn,
  insurancePaymentPostedColumn,
  claimPaymentRemarkCodesColumn,
  relatedAllocationsColumn,
];
